import { useEffect, useMemo, useRef, useState } from "react";
import type { ClientMeta } from "./types";
import { Header } from "./Header";
import { Viewer } from "./Viewer";
import { LeftPanel } from "./panels/LeftPanel";
import { RightPanel } from "./panels/RightPanel";
import { resolveBundleBase, resolveBundleFile } from "./bundle";

export default function App() {
  const [meta, setMeta] = useState<ClientMeta | null>(null);
  const [error, setError] = useState<string | null>(null);

  const base = useMemo(resolveBundleBase, []);
  // mesh.glb is shared across passes; lives at the mission root, one
  // level up from the per-pass bundle base.
  const meshUrl = useMemo(
    () => resolveBundleFile(base, "../mesh.glb"),
    [base]
  );
  const resetRef = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    fetch(`${base}client_meta.json`)
      .then((r) => {
        if (!r.ok) {
          throw new Error(`${r.status} ${r.statusText}`);
        }
        return r.json() as Promise<ClientMeta>;
      })
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [base]);

  return (
    <div className="h-screen flex flex-col bg-cloud text-ink overflow-hidden">
      <Header meta={meta} error={error} />

      <main className="flex-1 flex overflow-hidden">
        {meta ? (
          <>
            <LeftPanel meta={meta} />
            <div className="flex-1 relative bg-cloud">
              <Viewer meshUrl={meshUrl} base={base} resetRef={resetRef} />
              <ResetButton onClick={() => resetRef.current()} />
            </div>
            <RightPanel />
          </>
        ) : error ? (
          <div className="p-12">
            <p style={{ color: "var(--color-slate)", fontSize: "14px" }}>
              Failed to load mission.
            </p>
            <p
              className="mt-2"
              style={{ color: "var(--color-stone)", fontSize: "12px" }}
            >
              {error}
            </p>
          </div>
        ) : (
          <div className="p-12">
            <p style={{ color: "var(--color-stone)", fontSize: "14px" }}>
              Loading mission…
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

interface ResetButtonProps {
  onClick: () => void;
}

function ResetButton({ onClick }: ResetButtonProps) {
  return (
    <button
      onClick={onClick}
      className="absolute top-4 right-4 px-4 py-2 rounded-md transition-all"
      style={{
        background: "white",
        color: "var(--color-ink)",
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        boxShadow:
          "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.06)",
        border: "1px solid var(--color-mist)",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          "var(--color-cream)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = "white";
      }}
    >
      ↺ Reset View
    </button>
  );
}
